import { useState, useEffect } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import SlicerProcessForm from '../components/SlicerProcessForm';
import PageHeader from '../components/ui/PageHeader';

const getDisplayName = (name) => {
  if (!name) return '';
  if (typeof name === 'string') return name;
  if (typeof name === 'object') return Object.values(name).find(v => v) || '';
  return String(name);
};

export default function SlicerQualitySettings() {
  const [processes, setProcesses] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const fetchProcesses = async () => {
    try {
      setLoading(true);
      const res = await api.get('/auth/slicer-settings/processes');
      setProcesses(res.data.data || []);
    } catch {
      console.error('Failed to fetch quality settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProcesses(); }, []);

  useEffect(() => {
    let result = [...processes];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(p => Object.values(p.name || {}).some(v => v?.toLowerCase?.().includes(s)));
    }
    setFiltered(result);
  }, [processes, search]);

  const handleSave = async (data) => {
    const isFormData = data instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    if (editing) {
      await api.patch(`/auth/slicer-settings/processes/${editing._id}`, data, config);
    } else {
      await api.post('/auth/slicer-settings/processes', data, config);
    }
    setShowForm(false);
    setEditing(null);
    fetchProcesses();
  };

  const handleDelete = async (process) => {
    if (!window.confirm(`Deactivate "${getDisplayName(process.name)}"?`)) return;
    try {
      await api.patch(`/auth/slicer-settings/processes/${process._id}`, { isActive: false });
      fetchProcesses();
    } catch {
      alert('Failed to deactivate');
    }
  };

  const columns = [
    { key: 'thumbnail', label: '', render: (val) => val?.url ? <img src={val.url} alt="" className="h-8 w-8 object-contain rounded" onError={(e) => { e.target.style.display = 'none'; }} /> : null },
    { key: 'name', label: 'Name', sortable: true, render: (val) => getDisplayName(val) },
    { key: 'settings', label: 'Configured machines', render: (val) => Array.isArray(val) ? val.length : 0 },
    { key: 'quality', label: 'Nozzle', render: (val) => val?.nozzle || 'N/A' },
    { key: 'quality', label: 'Layer Height', render: (val) => val?.layerHeight || 'N/A' },
    { key: 'isActive', label: 'Active', render: (val) => val ? '✓' : '✗' },
    { key: 'createdAt', label: 'Created', sortable: true, render: (val) => new Date(val).toLocaleDateString() },
  ];

  if (loading && processes.length === 0) return <div className="text-center py-8 text-sm text-slate-500">Loading...</div>;

  return (
    <div className="space-y-4">
      <PageHeader title="Quality Settings" description="Slicer quality profiles per machine and nozzle" />

      {showForm ? (
        <SlicerProcessForm process={editing} onSave={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} />
      ) : (
        <DataTable
          data={filtered}
          columns={columns}
          onAdd={() => { setEditing(null); setShowForm(true); }}
          onEdit={(p) => { setEditing(p); setShowForm(true); }}
          onDelete={handleDelete}
          renderFilters={() => (
            <input
              type="text"
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-4 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          )}
        />
      )}
    </div>
  );
}
